import type { ScenarioOrder } from '../../../lib/planningData';
import { convertToDefaultCurrency } from '../../../state/zustand';
import { type MarcuraPortCosts } from '../../marcura/context';
import type { Currency } from '@seaber/common/graphql/types';
import type { LocationId } from '@seaber/common/core/entries';

export type PortCostByVcMaps = Map<
  string,
  {
    cost: number;
    currency: string;
    estimatedCost?: number;
  }
>;

export function getIntegrationPortCosts(
  locationId: LocationId,
  vesselWeight: number | null | undefined,
  orders: ScenarioOrder[],
  portCostByVcMaps: PortCostByVcMaps,
  portCostMarcuraMaps: MarcuraPortCosts,
  baseCurrency: Currency,
) {
  let cost:
    | { value: number; currency: string; source?: 'PortLog' }
    | undefined = undefined;

  for (const order of orders) {
    const vc = order.incomingVoyageCharterRelation;
    const assortmentId = order.assortmentByAssortmentId?.id;
    if (portCostMarcuraMaps && assortmentId && vesselWeight) {
      const portCosts = portCostMarcuraMaps.get(locationId, assortmentId);
      if (portCosts?.data.costs.length) {
        const portCost = portCosts.data.costs.find(
          (c) => vesselWeight >= c.dwtMin && vesselWeight <= c.dwtMax,
        );
        // NOTE: This seems a bit fishy, for both loading/discharge port call
        // we apply either the loading or discharge port cost
        if (portCost && (portCost.costLoading || portCost.costDischarging)) {
          const value =
            convertToDefaultCurrency(
              Math.max(
                portCost.costLoading || 0,
                portCost.costDischarging || 0,
              ),
              baseCurrency,
            ) ?? 0;

          if (!value) continue;

          // Prioritize the highest value
          if (!cost || (cost?.value ?? 0) < value) {
            cost = {
              value,
              currency: baseCurrency,
              source: 'PortLog',
            };
          }
        }
      }
    }

    if (cost) continue;
    if (vc) {
      const vcId = vc.voyageCharterId;
      const portCost = portCostByVcMaps.get(`${vcId}-${locationId}`);
      if (!portCost) continue;
      const value =
        convertToDefaultCurrency(
          portCost.cost ?? portCost.estimatedCost ?? 0,
          portCost.currency,
        ) ?? 0;

      cost = { value, currency: portCost.currency };
    }
  }

  return cost;
}
